import React, { useEffect, useState } from 'react';
import {
  SimpleForm,
  FormDataConsumer,
  SelectInput,
  NumberInput,
  number,
  minValue,
  useTranslate,
  required,
} from 'react-admin';
import { useForm } from 'react-final-form';
import { isEmpty } from 'lodash';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Accordion,
  AccordionDetails,
  Grid,
  IconButton,
  Button,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import { AmountField, BottomToolbar, TextField, AccordionSummary } from '~/components';
import { AmountInput, CurrencySelect } from '~/components/ra';
import { l } from '~/resources/pricings/PricingShow';
import { useEditModeAccordionClass } from '~/hooks/useEditModeContext';
import { ByFeature } from '..';
import lodashGet from 'lodash/get';
import { Rule } from '~/types/pricings';
import { BooleanInput } from 'react-admin';
import { TextInput } from 'react-admin';
import { updateRuleCurrency } from '~/utils/pricings';

// const INITIAL_RULE_BOUNDARY_VALUE = 0;
const INITIAL_RULE_AMOUNT_VALUE = 0;

export const compareRules = (firstRule: Rule, secondRule: Rule) =>
  firstRule.minCounterValue - secondRule.minCounterValue;

interface CounterBasedProps {
  formData: any;
  getSource: (key: string) => string;
  schema: ByFeature;
  permanentSelectedCurrency: string;
}

const CounterBased = (props: CounterBasedProps) => {
  const { formData, getSource, schema, permanentSelectedCurrency } = props;
  const translate = useTranslate();
  const t = (key: string, args: any = 1): string => translate(l(key), args);
  const classes = useStyles();
  const editModeAccordionClass = useEditModeAccordionClass();
  const form = useForm();
  useEffect(() => {
    form.destroyOnUnregister = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const feeNameKey: string = schema.propertyKey;

  const counterBasedCurrency = formData[feeNameKey]?.currency || permanentSelectedCurrency || '';

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<{ [x: string]: boolean }>({});
  const [editRuleIndex, setEditRuleIndex] = useState<number>(-1);
  const [isEditingDefaultRule, setIsEditingDefaultRule] = useState(false);

  const rules: Rule[] = lodashGet(formData, `${[feeNameKey]}.rules`);
  const defaultRule = lodashGet(formData, `${[feeNameKey]}.defaultRule`);

  // Setting default value rule
  useEffect(() => {
    if (isEmpty(defaultRule) && counterBasedCurrency) {
      console.log('anaiiSetting default rule');
      form.change(`${[feeNameKey]}.defaultRule`, {
        type: 'fixed',
        amount: { currency: permanentSelectedCurrency, value: INITIAL_RULE_AMOUNT_VALUE },
        shouldIncreaseCounter: false,
      });
      form.change(`${[feeNameKey]}.currency`, permanentSelectedCurrency);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCounterBasedCurrency = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCurrency = e.target.value;
    if (!newCurrency) {
      form.change(`${[feeNameKey]}.rules`, undefined);
      form.change(`${[feeNameKey]}.defaultRule`, undefined);
    } else if (isEmpty(defaultRule) && newCurrency) {
      form.change(`${[feeNameKey]}.defaultRule`, {
        type: 'fixed',
        amount: { currency: newCurrency, value: INITIAL_RULE_AMOUNT_VALUE },
        shouldIncreaseCounter: false,
      });
    } else {
      const newRules = (rules || []).map((rule) => updateRuleCurrency(rule, newCurrency));
      const currentDefaultRule = formData[feeNameKey]?.defaultRule;
      if (currentDefaultRule) {
        form.change(
          `${[feeNameKey]}.defaultRule`,
          updateRuleCurrency(currentDefaultRule, newCurrency)
        );
      }
      form.change(`${[feeNameKey]}.rules`, newRules);
    }
  };

  const handleOpen = (isDefault = false) => {
    setIsEditingDefaultRule(isDefault);
    setOpen(true);
  };

  const handleClose = () => {
    editRuleIndex > -1 && setEditRuleIndex(-1);
    setExpanded({});
    setOpen(false);
    // Wrap in timeout to avoid wrong form show in the moment of closing dialog
    setTimeout(() => {
      setIsEditingDefaultRule(false);
    }, 200);
  };

  const handleChange = (key: string, isExpanded: boolean) => () => {
    setExpanded((prevProps) => ({ ...prevProps, [key]: !isExpanded }));
  };

  const handleEdit = (index: number) => {
    index > -1 && setEditRuleIndex(index);
    handleOpen(false);
  };

  const handleDelete = (index: number) => {
    if (rules) {
      form.change(
        `${[feeNameKey]}.rules`,
        rules.filter((_item, i) => i !== index)
      );
    }
  };

  const newRuleIndex: number = rules?.length || 0;

  const getRuleSource = (index: number, source: string): string => `rules[${index}].${source}`;
  const getDefaultRuleSource = (source: string) => `defaultRule.${source}`;

  const handleSubmit = (newRecord: any): void => {
    if (isEditingDefaultRule) {
      form.change(`${[feeNameKey]}.defaultRule`, { ...newRecord[feeNameKey].defaultRule });
    } else if (newRecord[feeNameKey].rules) {
      form.change(`${[feeNameKey]}.rules`, [...newRecord[feeNameKey].rules]);
    }
    handleClose();
  };

  const MAX_RULES = 10;
  const isMaxCountOfRules = formData[feeNameKey]?.rules?.length >= MAX_RULES;
  return (
    <>
      <CurrencySelect
        onChange={handleCounterBasedCurrency}
        label={t('currency')}
        record={formData}
        source={getSource('currency')}
        validate={required()}
        disabled={!!permanentSelectedCurrency}
      />
      <TextInput
        source={getSource('dynamicTypeCounterReference')}
        label={t('dynamicTypeCounterReference')}
        validate={required()}
      />
      {counterBasedCurrency && (
        <Grid container spacing={2}>
          {defaultRule && (
            <Grid item xs={12}>
              <Accordion
                expanded={expanded[`panel-default`]}
                onChange={handleChange(`panel-default`, expanded[`panel-default`])}
                className={editModeAccordionClass}
              >
                <AccordionSummary
                  aria-controls={`default-content`}
                  id={`default-header`}
                  expanded={expanded[`panel-default`]}
                  heading={<div className={classes.ruleHeader}>{t('defaultRule')}</div>}
                  actionNode={
                    <>
                      <IconButton
                        type='button'
                        size='small'
                        onClick={() => handleOpen(true)}
                        className={classes.actionButton}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        type='button'
                        size='small'
                        disabled
                        className={classes.actionButton}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  }
                />
                <AccordionDetails
                  key='external-sources-details'
                  className={classes.AccordionDetails}
                >
                  <CounterBasedFormField ruleItem={formData[feeNameKey]?.defaultRule} isDefault />
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}
          {formData[feeNameKey]?.rules &&
            !isEmpty(formData[feeNameKey]?.rules) &&
            formData[feeNameKey]?.rules
              ?.sort((firstRule: Rule, secondRule: Rule) => {
                return compareRules(firstRule, secondRule);
              })
              ?.map((item: Rule, index: number) => {
                const isExpanded = Boolean(expanded[`panel-${index}`]);
                return (
                  <Grid key={index} item xs={12}>
                    <Accordion
                      expanded={isExpanded}
                      onChange={handleChange(`panel-${index}`, isExpanded)}
                      key={index}
                      className={editModeAccordionClass}
                    >
                      <AccordionSummary
                        aria-controls={`${index}-content`}
                        id={`${index}-header`}
                        expanded={isExpanded}
                        heading={
                          <TextField label={t('rule', { num: index + 1 })}>
                            {t('minCounterMaxCounter', {
                              min: item?.minCounterValue,
                              max: item?.maxCounterValue,
                            })}
                          </TextField>
                        }
                        actionNode={
                          <>
                            <IconButton
                              type='button'
                              size='small'
                              onClick={() => handleEdit(index)}
                              className={classes.actionButton}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              type='button'
                              size='small'
                              onClick={() => handleDelete(index)}
                              className={classes.actionButton}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        }
                      />
                      <AccordionDetails
                        key='external-sources-details'
                        className={classes.AccordionDetails}
                      >
                        <CounterBasedFormField ruleItem={item} />
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                );
              })}
          {counterBasedCurrency && (
            <Grid item xs={12}>
              <Tooltip title={t('tenRulesOnly')} disableHoverListener={!isMaxCountOfRules}>
                <span>
                  <Button
                    aria-controls='simple-menu'
                    aria-haspopup='true'
                    onClick={() => handleOpen(false)}
                    disabled={isMaxCountOfRules}
                    color='primary'
                    startIcon={<AddIcon />}
                  >
                    {t('addNewRule')}
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          )}
          <Dialog onClose={handleClose} aria-labelledby='simple-dialog-title' open={open}>
            <DialogTitle id='simple-dialog-title'>
              {editRuleIndex > -1
                ? t('editRule', { num: editRuleIndex + 1 })
                : isEditingDefaultRule
                ? t('editDefaultRule')
                : t('addNewRule')}
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <SimpleForm
                redirect={false}
                margin='none'
                className={classes.form}
                record={formData}
                toolbar={<BottomToolbar onSave={handleSubmit} onClose={handleClose} />}
              >
                <FormDataConsumer>
                  {({ formData }: any) => {
                    return (
                      <CounterBasedFormInput
                        getSource={getSource}
                        getRuleSource={getRuleSource}
                        getDefaultRuleSource={getDefaultRuleSource}
                        newRuleIndex={newRuleIndex}
                        counterBasedCurrency={counterBasedCurrency}
                        // feeNameKey={feeNameKey}
                        formData={formData}
                        editRuleIndex={editRuleIndex}
                        isDefault={isEditingDefaultRule}
                      />
                    );
                  }}
                </FormDataConsumer>
              </SimpleForm>
            </DialogContent>
          </Dialog>
        </Grid>
      )}
    </>
  );
};

interface CounterBasedFormInputProps {
  formData: any;
  getSource: (key: string) => string;
  getRuleSource: (index: number, source: string) => string;
  getDefaultRuleSource: (source: string) => string;
  newRuleIndex: number;
  counterBasedCurrency: string;
  // feeNameKey: string;
  editRuleIndex: number;
  isDefault?: boolean;
}

const CounterBasedFormInput = (props: CounterBasedFormInputProps) => {
  const {
    getSource,
    getRuleSource,
    getDefaultRuleSource,
    newRuleIndex,
    counterBasedCurrency,
    // feeNameKey,
    formData,
    editRuleIndex,
    isDefault = false,
  } = props;
  const form = useForm();
  useEffect(() => {
    form.destroyOnUnregister = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const translate = useTranslate();
  const t = (key: string, variant: any = 1): string => translate(l(key), variant);

  const itemIndex = editRuleIndex > -1 ? editRuleIndex : newRuleIndex;
  const selectedType = isDefault
    ? lodashGet(formData, getSource(getDefaultRuleSource('type')))
    : lodashGet(formData, getSource(getRuleSource(itemIndex, 'type')));
  return (
    <>
      {!isDefault && (
        <>
          <NumberInput
            source={getSource(getRuleSource(itemIndex, 'minCounterValue'))}
            label={t('minCounterValueIncl')}
            validate={[number(), required()]}
            defaultValue={0}
          />
          <NumberInput
            source={getSource(getRuleSource(itemIndex, 'maxCounterValue'))}
            label={t('maxCounterValueExcl')}
            validate={[number(), required()]}
          />
          <AmountInput
            source={getSource(getRuleSource(itemIndex, 'minTransferAmount'))}
            amountLabel={t('minTransferAmountIncl')}
            permanentSelectedCurrency={counterBasedCurrency}
            fullWidth
            positiveOnly
            currencyDisabled
          />
          <AmountInput
            source={getSource(getRuleSource(itemIndex, 'maxTransferAmount'))}
            amountLabel={t('maxTransferAmountExcl')}
            permanentSelectedCurrency={counterBasedCurrency}
            fullWidth
            positiveOnly
            currencyDisabled
          />
        </>
      )}
      <SelectInput
        source={getSource(
          isDefault ? getDefaultRuleSource('type') : getRuleSource(itemIndex, 'type')
        )}
        label={t('feeAlgorithm')}
        choices={[
          { id: 'fixed', name: t('fixed') },
          { id: 'percent', name: t('percent', 2) },
          { id: 'fixedPlusPercent', name: t('fixedPlusPercent') },
        ]}
        value={selectedType}
        validate={required()}
      />
      {selectedType === 'fixed' && (
        <AmountInput
          source={getSource(
            isDefault ? getDefaultRuleSource('amount') : getRuleSource(itemIndex, 'amount')
          )}
          amountLabel={t('amount')}
          permanentSelectedCurrency={counterBasedCurrency}
          required
          fullWidth
          positiveOnly
          currencyDisabled
        />
      )}
      {selectedType === 'percent' && (
        <Grid container>
          <Grid item xs={12}>
            <NumberInput
              source={getSource(
                isDefault ? getDefaultRuleSource('percent') : getRuleSource(itemIndex, 'percent')
              )}
              label={t('percent', 2)}
              min={0}
              validate={[number(), minValue(0), required()]}
            />
          </Grid>
          <Grid item xs={12}>
            <AmountInput
              source={getSource(
                isDefault
                  ? getDefaultRuleSource('minAmount')
                  : getRuleSource(itemIndex, 'minAmount')
              )}
              amountLabel={t('minAmount')}
              permanentSelectedCurrency={counterBasedCurrency}
              fullWidth
              positiveOnly
              currencyDisabled
            />
            <AmountInput
              source={getSource(
                isDefault
                  ? getDefaultRuleSource('maxAmount')
                  : getRuleSource(itemIndex, 'maxAmount')
              )}
              amountLabel={t('maxAmount')}
              permanentSelectedCurrency={counterBasedCurrency}
              fullWidth
              positiveOnly
              currencyDisabled
            />
          </Grid>
        </Grid>
      )}
      {selectedType === 'fixedPlusPercent' && (
        <Grid container>
          <Grid item xs={12}>
            <AmountInput
              source={getSource(
                isDefault ? getDefaultRuleSource('amount') : getRuleSource(itemIndex, 'amount')
              )}
              amountLabel={t('amount')}
              permanentSelectedCurrency={counterBasedCurrency}
              required
              fullWidth
              positiveOnly
              currencyDisabled
            />
          </Grid>
          <Grid item xs={12}>
            <NumberInput
              source={getSource(
                isDefault ? getDefaultRuleSource('percent') : getRuleSource(itemIndex, 'percent')
              )}
              label={t('percent', 2)}
              min={0}
              validate={[number(), minValue(0), required()]}
            />
          </Grid>
          <Grid item xs={12}>
            <AmountInput
              source={getSource(
                isDefault
                  ? getDefaultRuleSource('minAmount')
                  : getRuleSource(itemIndex, 'minAmount')
              )}
              amountLabel={t('minAmountOfPercentPart')}
              permanentSelectedCurrency={counterBasedCurrency}
              fullWidth
              positiveOnly
              currencyDisabled
            />
            <AmountInput
              source={getSource(
                isDefault
                  ? getDefaultRuleSource('maxAmount')
                  : getRuleSource(itemIndex, 'maxAmount')
              )}
              amountLabel={t('maxAmountOfPercentPart')}
              permanentSelectedCurrency={counterBasedCurrency}
              fullWidth
              positiveOnly
              currencyDisabled
            />
          </Grid>
        </Grid>
      )}
      <BooleanInput
        source={getSource(
          isDefault
            ? getDefaultRuleSource('shouldIncreaseCounter')
            : getRuleSource(itemIndex, 'shouldIncreaseCounter')
        )}
        label={t('shouldIncreaseCounter')}
        validate={required()}
        defaultValue={isDefault ? false : true}
      />
    </>
  );
};

interface CounterBasedFormFieldProps {
  ruleItem: Rule;
  isDefault?: boolean;
}

const CounterBasedFormField = (props: CounterBasedFormFieldProps) => {
  const { ruleItem, isDefault = false } = props;
  const translate = useTranslate();
  const t = (key: string, variant: number = 1): string => translate(l(key), variant);
  return (
    <Grid container direction='column' spacing={2}>
      {!isDefault && (
        <Grid container item direction='row' spacing={2}>
          <Grid item xs={6}>
            <TextField label={t('minTransferAmount')}>
              <AmountField amount={ruleItem?.minTransferAmount} />
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField label={t('maxTransferAmount')}>
              <AmountField amount={ruleItem?.maxTransferAmount} />
            </TextField>
          </Grid>
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField label={t('feeAlgorithm')}>{t(ruleItem?.type, 2)}</TextField>
      </Grid>
      {ruleItem?.type === 'fixed' && (
        <Grid item xs={12}>
          <TextField label={t('amount')}>
            <AmountField amount={ruleItem?.amount} />
          </TextField>
        </Grid>
      )}
      {ruleItem?.type === 'percent' && (
        <>
          <Grid item xs={12}>
            <TextField label={t('percent', 2)}>{ruleItem?.percent?.toString() + '%'}</TextField>
          </Grid>
          <Grid item>
            <Grid container direction='row' spacing={2}>
              <Grid item xs={6}>
                <TextField label={t('minAmount')}>
                  <AmountField amount={ruleItem?.minAmount} />
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField label={t('maxAmount')}>
                  <AmountField amount={ruleItem?.maxAmount} />
                </TextField>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
      {ruleItem?.type === 'fixedPlusPercent' && (
        <>
          <Grid item xs={12}>
            <TextField label={t('amount')}>
              <AmountField amount={ruleItem?.amount} />
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField label={t('percent', 2)}>{ruleItem?.percent?.toString() + '%'}</TextField>
          </Grid>
          <Grid item>
            <Grid container direction='row' spacing={2}>
              <Grid item xs={6}>
                <TextField label={t('minAmountOfPercentPart')}>
                  <AmountField amount={ruleItem?.minAmount} />
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField label={t('maxAmountOfPercentPart')}>
                  <AmountField amount={ruleItem?.maxAmount} />
                </TextField>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <TextField label={t('shouldIncreaseCounter')}>
          {ruleItem?.shouldIncreaseCounter ? t('yes') : t('no')}
        </TextField>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles((theme) => ({
  AccordionDetails: {
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(3),
    display: 'block',
  },
  form: {
    '& > div': {
      padding: 0,
      '&:first-child': {
        paddingTop: 0,
      },
    },
  },
  dialogContent: {
    paddingTop: 0,
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    width: 360,
    boxSizing: 'border-box',
  },
  actionButton: {
    color: theme.palette.text.primary,
  },
  ruleHeader: {
    fontSize: 10,
    fontWeight: 'normal',
    marginBottom: 4,
  },
}));

export default CounterBased;
